import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Modal from '@material-ui/core/Modal';
import {
  Grid,
  Typography,
  List,
  ListItem,
  TextField,
  Portal,
  ClickAwayListener,
} from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { makeStyles, Theme } from '@material-ui/core/styles';
import postOnSlack from 'src/core/postOnSlack';
import atm from '../assets/atm.svg';
import singleMarketImage from '../assets/singleMarketImage.svg';
import multiMarketImage from '../assets/multiMarketImage.svg';
import globalImage from '../assets/globalImage.svg';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Link,
  Box,
  ButtonGroup,
  Button,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Backdrop,
  Tooltip,
  Divider,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  COLOR_BLACK,
  COLOR_RED,
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_GREY,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_WHITE,
  ProductIDOptions,
  COLOR_TOOLTIP_BACKGROUND,
  PAYMENT_MODAL_GREEN,
  PAYMENT_MODAL_GREEN_HOVER,
  countryListWithCode,
  activeListingAsPerCountryTier,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  exchangeRates,
  countryCodeToNumberOfListingsCountryPlan,
  SUBSCRIPTION_MODAL_DATA_GRID_WIDTH,
  SUBSCRIPTION_MODAL_DATA_GRID_WIDTH_FOR_MOBILE,
  SUBSCRIPTION_TABLE_ROWS,
  SUBSCRIPTION_TABLE_ROWS_FOR_MOBILE_VIEW,
  // CurrencyWithSubscriptionPriceLegacy,
} from 'src/const';
import { Payment } from '../HOC/Payment';
import { SubscriptionModalPropTypes } from 'src/interfaces/iproptypes';
import {
  calculateCouponChange,
  checkPromocodeValidity,
  formatCurrency,
  formatCurrencyBasedOnCountryCode,
  getCountryDataFromCountryCode,
  updateUserOpenedSubscriptionModalInDynamoDB,
} from 'src/utils/functions';
import { InfoTooltip } from '../HOC/Tooltips';
import { callApi } from 'src/core/callApi';

import ErrorIcon from '@mui/icons-material/Error';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  COLOR_ERROR_BACKGROUNG,
  COLOR_GREY_200,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_PRIMARY_2_200,
  COLOR_SUCCESS,
  COLOR_SUCCESS_1,
  COLOR_SUCCESS_1_HOVER,
  COLOR_SUCCESS_BACKGROUNG,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  SIDEBAR_COLOR,
  SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
  SUBSCRIPTION_MODAL_DISCOUNT_BACKGROUND_COLOR,
  SUBSCRIPTION_MODAL_DISCOUNT_TEXT_COLOR,
  SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
} from 'src/colors';
import AutocompleteSearchbarforCountryWithCode from './AutocompleteSearchbarForCountry';
import TooltipCard from '../HOC/Cards/TooltipCard';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DemoBookingForm from '../Components/Forms/DemoBookingForm';
import SubscriptionModalDataGrid from './subscriptionModalDataGrid';

const useStyles = makeStyles((theme: Theme) => ({
  paperModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100vw',
    overflowY: 'scroll',
    overflowX: 'hidden',
    boxShadow: theme.shadows[5],
    backgroundColor: '#fff',
    [theme.breakpoints.down(960)]: {
      height: '60%',
      overflowY: 'scroll',
      padding: theme.spacing(4, 4, 4),
    },
  },
  paymentBorder: {
    border: `2px solid ${COLOR_GREY}`,
    borderRadius: 10,
    padding: theme.spacing(2, 3, 2),
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down(960)]: {
      marginBottom: 40,
    },
  },
  couponField: {
    marginTop: 10,
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  switchContainer: {
    backgroundColor: COLOR_PRIMARY,
    borderRadius: 50,
    width: 115,
    height: 25,
  },
  switch: {
    borderRadius: 50,
    height: 20,
    width: 55,
    padding: 5,
  },
  switchIcon: {
    color: COLOR_WHITE,
  },
  switchIconActive: {
    color: COLOR_BLACK,
  },
  switchActive: {
    backgroundColor: COLOR_WHITE,
    '&:hover': {
      backgroundColor: COLOR_WHITE,
    },
  },
}));

const getMarketsText = (product: string) => {
  const markets: number = (ProductIDOptions[product] as { markets: number }).markets;
  if (markets > 1) {
    return `${markets} markets`;
  }
  return '1 market';
};

const productNameToTitleMap = district => {
  return {
    starter: district && !district.includes('Brighton and Hove') ? district : '1 Market',
    standard:
      district && !district.includes('Brighton and Hove') ? (
        <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '50px',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          >
            {district}
          </span>
          &nbsp;+ 4 Markets
        </span>
      ) : (
        '5 Markets'
      ),
    enterprise:
      district && !district.includes('Brighton and Hove') ? (
        <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '50px',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          >
            {district}
          </span>
          &nbsp;+ 29 Markets
        </span>
      ) : (
        '30 Markets'
      ),
    monthly: 'Monthly',
    yearly: 'Yearly',
    quarterly: 'Quarterly',
  };
};

const marketLogoMap = {
  starter: singleMarketImage,
  standard: multiMarketImage,
  enterprise: globalImage,
};

const chipTextMap = {
  starter: 'Basic',
  standard: 'Popular',
  enterprise: '',
};

const productStateVarToNameMap = {
  pro: 'Insight Pro',
  expert: 'Analytics Expert',
};

const PlanSwitchGroup = ({
  values,
  type,
  setType,
  float,
  district,
  duration,
  discountPercentForYearly,
  discountPercentForQuarterly,
  monthlyButtonDisabled,
  product,
}: {
  values: string[];
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
  float: string;
  district: string;
  duration: string;
  discountPercentForYearly: number;
  discountPercentForQuarterly: number;
  monthlyButtonDisabled: boolean;
  product: string;
}) => {
  const buttonStyle: any = {
    background: PAYMENT_MODAL_GREEN,
    // borderRadius: 200,
    color: '#000',
    border: 'none !important',
    textTransformation: 'capitalize',
    transition: 'background 0.3s ease',
    '&:hover': {
      background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
    },
  };
  return (
    <Box
      // variant="contained"
      // size="small"
      sx={{
        borderRadius: '10px',
        background: COLOR_PRIMARY_2,
        float,
        padding: '7px 3px',
      }}
    >
      {values.map((data, i) =>
        monthlyButtonDisabled && data == 'monthly' ? (
          <Tooltip title="Global plan is only available with yearly plan.">
            <Button
              key={i}
              // size="medium"
              sx={{
                ...buttonStyle,
                background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
                color: COLOR_GREY_400,
                '&:hover': {
                  background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
                  color: COLOR_GREY_400,
                },
                borderRadius: '10px',
                padding: '2px 8px !important',
              }}
            >
              <Typography style={{ fontSize: '16px' }}>
                {productNameToTitleMap(district)[data.toLowerCase()]
                  ? productNameToTitleMap(district)[data.toLowerCase()]
                  : data}
              </Typography>
            </Button>
          </Tooltip>
        ) : (
          <Button
            key={i}
            // size="sma10COLOR_PRIMARY_2_100
            onClick={() => {
              setType(data);
            }}
            sx={{
              ...buttonStyle,
              background: type === data ? 'white' : COLOR_PRIMARY_2,
              color: type === data ? 'black' : COLOR_PRIMARY_2_100,
              '&:hover': {
                background: type === data ? 'white' : COLOR_PRIMARY_2,
                color: type === data ? 'black' : 'white',
              },
              borderRadius: '10px',
              padding: '7px 10px !important',
              margin: '0 5px !important',
            }}
          >
            <Typography style={{ fontSize: '16px', fontWeight: 500 }}>
              {productNameToTitleMap(district)[data.toLowerCase()]
                ? productNameToTitleMap(district)[data.toLowerCase()]
                : data}
            </Typography>
            {data == 'yearly' || data == 'quarterly' ? (
              <Typography
                style={{
                  fontSize: '12px',
                  backgroundColor: SUBSCRIPTION_MODAL_DISCOUNT_BACKGROUND_COLOR,
                  color: SUBSCRIPTION_MODAL_DISCOUNT_TEXT_COLOR,
                  marginLeft: '5px',
                  borderRadius: '20px',
                  padding: '2px 8px',
                  fontWeight: 'bold',
                }}
              >
                {` ${
                  data == 'yearly' ? discountPercentForYearly : discountPercentForQuarterly
                }% Off`}
              </Typography>
            ) : (
              <></>
            )}
          </Button>
        )
      )}
    </Box>
  );
};

const MarketSwitchGroup = ({
  values,
  type,
  setType,
  float,
  district,
  isMobile,
}: {
  values: string[];
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
  float: string;
  district: string;
  isMobile: boolean;
}) => {
  return (
    <Box>
      {values.map((data, i) => (
        <Button
          key={i}
          // size="small"
          onClick={() => {
            setType(data);
          }}
          sx={{
            background: type === data ? COLOR_PRIMARY_2 : SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
            color: type === data ? '#fff' : '#000',
            margin: '10px',
            '&:hover': {
              color: type === data ? '#fff' : '#000',
              background:
                type === data ? COLOR_PRIMARY_2 : SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
            },
            transform: type === data ? 'scaleY(1.05)' : 'scaleY(1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              width: type === data ? '150px' : '100px',
              maxWidth: isMobile ? '100px' : '150px',
              height: '100px',
              padding: '10px',
            }}
          >
            {chipTextMap[data] && (
              <Box
                sx={{
                  height: '15px',
                  padding: '5px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  position: 'absolute',
                  top: '-10px',
                  left: '10px',
                }}
              >
                <Typography style={{ fontSize: '12px', color: '#000', fontWeight: 500 }}>
                  {chipTextMap[data]}
                </Typography>
              </Box>
            )}

            {type === data && (
              <Box
                sx={{
                  padding: '4px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '200px',
                  position: 'absolute',
                  top: '-13px',
                  right: '10px',
                }}
              >
                <Grid
                  item
                  style={{
                    background: COLOR_PRIMARY_2,
                    borderRadius: 200,
                    padding: '2px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    stroke: '#fff',
                    strokeWidth: '2px',
                  }}
                >
                  <DoneRoundedIcon sx={{ color: '#fff', fontWeight: 'bold', fontSize: '16px' }} />
                </Grid>
              </Box>
            )}
            <img
              src={marketLogoMap[data]}
              style={{
                height: '60px',
                filter: type === data ? 'invert(100%)' : 'invert(0%)',
              }}
            />
            <Typography style={{ fontSize: '16px', fontWeight: 500, marginTop: '5px' }}>
              {productNameToTitleMap(district)[data.toLowerCase()]
                ? productNameToTitleMap(district)[data.toLowerCase()]
                : data}
            </Typography>
          </Box>
        </Button>
      ))}
    </Box>
  );
};

const SubscriptionModal = ({
  countryCode,
  city,
  district,
  getTokenThunk,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  paymentPlanQuery,
  paymentDurationQuery,
  setIsTailoredRegionOpen,
  user,
  setShowUpgradeSubscriptionModal,
}: SubscriptionModalPropTypes) => {
  const classes = useStyles();
  const isLoggedIn = Boolean(getTokenThunk);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('lg'));
  const [showDemoBookingForm, setShowDemoBookingForm] = useState<boolean>(false);
  const [isPromoCodeLoading, setIsPromoCodeLoading] = useState<boolean>(true);
  const [product, setProduct] = useState<string>('pro');
  const [duration, setDuration] = useState<string>('yearly');
  const [promoCode, setPromoCode] = useState<string>('');
  const [discountPercentForYearly, setDiscountPercentForYearly] = useState<number>(0);
  const [discountPercentForQuarterly, setDiscountPercentForQuarterly] = useState<number>(0);
  const [discountedPrice, setDiscountedPrice] = useState<null | number>(null);
  const [promoCodeDiscountsDict, setPromoCodeDiscountDict] = useState<any>(null);
  const [countryToUnlock, setCountryToUnlock] = useState<string | null>(null);
  // const [countryTier, setCountryTier] = useState<string>('tier3');
  const [defaultCountryToUnlock, setDefaultCountryToUnlock] = useState<any>(null);
  const userCurrentCountryCode = useSelector((state: any) => state.dashboard.userCountryCode);
  const [showFullRefundPolicyStatement, setShowFullRefundPolicyStatement] = useState<boolean>(
    false
  );
  const [monthlyButtonDisabled, setMonthlyButtonDisabled] = useState<boolean>(false);
  //userSignupCountryCode represent the country from where he/she signed up in our app
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];

  const promocodeTextFieldRef = useRef<HTMLInputElement | null>(null);
  const searchedMarketCountryCode = useSelector(
    state => (state as any).dashboard.search.data.countryCode
  );

  const [formHeadingFor, setFormHeadingFor] = useState<string>('demo');
  const paymentModalRef = useRef<HTMLDivElement>(null);

  const isSubscriptionUpgradable = user.subscriptionPlan == 'pro' && product === 'expert';

  const closeBookingForm = () => {
    setShowDemoBookingForm(false);
  };

  const openDemoBookingForm = () => {
    setShowDemoBookingForm(true);
  };

  // useEffect(() => {
  //   let page = window.location.pathname.split('/')[1];
  //   if (page === 'markets') {
  //     setProduct('enterprise');
  //   }
  // }, [window.location.pathname]);

  useEffect(() => {
    let timerId;

    if (subscriptionModalOpen) {
      timerId = setTimeout(() => {
        updateUserOpenedSubscriptionModalInDynamoDB();
      }, 3000);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [subscriptionModalOpen]);

  // useEffect(() => {
  //   if (
  //     user.credits > 0 ||
  //     user.estimatorCredits > 0 ||
  //     user.regions.length > 0 ||
  //     user.estimatorRegions.length > 0
  //   ) {
  //     setProduct('enterprise');
  //   }
  // }, [user]);

  useEffect(() => {
    let monthlyPrice = subscriptionPriceAccordingToUserLocalCurrency[product]['monthly'];
    let yearlyPricePerMonth = subscriptionPriceAccordingToUserLocalCurrency[product]['yearly'] / 12;
    let quarterlyPricePerMonth =
      subscriptionPriceAccordingToUserLocalCurrency[product]['quarterly'] / 3;

    let profitPercentile = Math.round(((monthlyPrice - yearlyPricePerMonth) / monthlyPrice) * 100);
    let profitPercentQuarterly = Math.round(
      ((monthlyPrice - quarterlyPricePerMonth) / monthlyPrice) * 100
    );

    setDiscountPercentForYearly(profitPercentile);
    setDiscountPercentForQuarterly(profitPercentQuarterly);
  }, [product, duration]);

  useEffect(() => {
    if (paymentPlanQuery) {
      setProduct(paymentPlanQuery);
    }
    if (paymentDurationQuery) {
      setDuration(paymentDurationQuery);
    }
  }, [paymentPlanQuery, paymentDurationQuery]);

  useEffect(() => {
    // if (promoCode) {
    //   calculateCouponChange(promoCode);
    // }
    window.history.pushState({}, '', `?payment-plan=${product}&payment-duration=${duration}`);
  }, [product, duration]);

  useEffect(() => {
    const getCoupons = async () => {
      let coupons = await callApi(
        // 'https://airbtics-resource.s3.us-east-2.amazonaws.com/coupons.json',
        'https://airbtics-resource.s3.us-east-2.amazonaws.com/couponsTest.json',
        {},
        false
      );

      //making all coupons capatalize
      for (const coupon in coupons) {
        if (!coupons[coupon.toUpperCase()]) {
          coupons[coupon.toUpperCase()] = coupons[coupon];
          delete coupons[coupon];
        }
      }
      setPromoCodeDiscountDict(coupons);
      setIsPromoCodeLoading(false);
    };
    getCoupons();

    if (searchedMarketCountryCode) {
      setDefaultCountryToUnlock(getCountryDataFromCountryCode(searchedMarketCountryCode));
    } else {
      setDefaultCountryToUnlock(getCountryDataFromCountryCode(userCurrentCountryCode));
    }
  }, []);

  const getProductTextCard = (
    text: JSX.Element,
    hasMoreText?: boolean,
    showFullRefundPolicyStatement?: boolean,
    setShowFullRefundPolicyStatement?: ((value: boolean) => void) | undefined
  ) => {
    return (
      <Grid
        container
        xs={12}
        spacing={1}
        style={{
          width: '100%',
          // padding: '4px 6px',
          // borderRadius: isMobile ? 10 : 200,
          // boxShadow: '0 2px 2px #ccc',
          flexWrap: 'nowrap',
        }}
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Grid
          item
          style={{
            background: PAYMENT_MODAL_GREEN,
            borderRadius: 200,
            // height: '20px',
            // width: '20px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            stroke: '#fff',
            strokeWidth: '2px',
            marginTop: '8px',
          }}
        >
          <DoneRoundedIcon sx={{ color: '#fff', fontWeight: 'bold', fontSize: '8px' }} />
        </Grid>
        <Grid item style={{ marginLeft: '5px' }}>
          <Typography style={{ fontSize: '16px' }}>{text}</Typography>
        </Grid>
        {hasMoreText && (
          <Box
            sx={{
              height: '25px',
              width: '25px',
              borderRadius: '20px',
              backgroundColor: COLOR_PRIMARY_2,
              marginTop: '10px',
              color: '#ffff',
              '&:hover': {
                cursor: 'pointer',
              },
            }}
            onClick={() => {
              setShowFullRefundPolicyStatement &&
                setShowFullRefundPolicyStatement(!showFullRefundPolicyStatement);
            }}
          >
            {!showFullRefundPolicyStatement ? (
              <ArrowRightIcon style={{ fontSize: '30px', marginTop: '-3px', marginLeft: '-2px' }} />
            ) : (
              <ArrowDropDownIcon
                style={{ fontSize: '30px', marginTop: '-3px', marginLeft: '-2px' }}
              />
            )}
          </Box>
        )}
      </Grid>
    );
  };

  const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentPromoCode: string = e.target.value;
    setPromoCode(_ => currentPromoCode);
    calculateCouponChange(
      currentPromoCode,
      promoCodeDiscountsDict,
      subscriptionPriceAccordingToUserLocalCurrency,
      duration,
      setDiscountedPrice,
      product
    );
  };

  const getCTAButtonInsidePolyStyle = (
    top: string,
    side: string,
    sideValue: string,
    rotation: number,
    background: string
  ): any => {
    return {
      width: '100px',
      height: '200px',
      position: 'absolute',
      top: top,
      [side]: sideValue,
      transform: `rotate(${rotation}deg)`,
      background: background,
    };
  };

  const getCountryNameFromCountryCode = countryCode => {
    let countryName;
    countryListWithCode.map(countryData => {
      if (countryData.code === countryCode) {
        countryName = countryData.label;
      }
    });
    return countryName;
  };

  return (
    // <Portal container={() => document.querySelector('body')!}>
    <Backdrop open={true} component="div" sx={{ zIndex: 1500 }}>
      <ClickAwayListener
        onClickAway={() => {
          setSubscriptionModalOpen(false);
          window.history.pushState({}, '', window.location.pathname);
        }}
      >
        <Grid container direction="column" alignItems="center" justifyContent="center">
          <DemoBookingForm
            matches={matches}
            user={user}
            showDemoBookingForm={showDemoBookingForm}
            closeBookingForm={closeBookingForm}
            headingFor={formHeadingFor}
          />
          <div
            ref={paymentModalRef}
            className={`${classes.paperModal} modal-custom`}
            style={{ height: '100vh', backgroundColor: SUBSCRIPTION_MODAL_DARK_THEME_COLOR }}
          >
            {/* Close BUTTON */}
            <div
              style={{
                position: 'absolute',
                right: '30px',
                top: isMobile || isMediumScreenSize ? '80px' : '30px',
                cursor: 'pointer',
              }}
              onClick={() => setSubscriptionModalOpen(false)}
            >
              <CloseRoundedIcon sx={{ fontSize: '40px', color: '#ffff' }} />
            </div>

            <Grid
              container
              direction="column"
              style={{
                alignItems: 'center',
              }}
            >
              <Grid
                container
                item
                style={{
                  ...{ maxWidth: '1600px' },
                  ...(isMobile || isMediumScreenSize ? { paddingTop: '40px' } : {}),
                }}
              >
                <Grid
                  item
                  xs={12}
                  style={{
                    minHeight: isMobile ? '75px' : '125px',
                    padding: isMobile || isMediumScreenSize ? '20px' : '20px 12%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    maxWidth: '1320px',
                  }}
                >
                  <Typography variant="h4" style={{ color: '#ffff', fontWeight: 500 }}>
                    Pricing
                  </Typography>
                  <Typography variant="h6" style={{ color: '#ffff', fontWeight: 400 }}>
                    Accurate and Actionable Data At Your Fingertips!
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                container
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '20px 20px 0px 0px',
                  padding: isMobile || isMediumScreenSize ? '20px' : '20px 5%',
                  maxWidth: '1600px',
                  position: 'relative',
                }}
              >
                {/* INFO */}
                <Grid
                  container
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  item
                  // style={{ paddingRight: '25px' }}
                  lg={12}
                  md={12}
                  xs={12}
                >
                  {/* <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    item
                    xs={12}
                    spacing={2}
                    style={{ width: '100%', marginBottom: '20px' }}
                  >
                    <Grid item xs={12}>
                      <MarketSwitchGroup
                        values={['starter', 'standard', 'enterprise']}
                        type={product}
                        setType={setProduct}
                        float={'left'}
                        district={district}
                        isMobile={isMobile}
                      />
                    </Grid>
                  </Grid> */}

                  <Grid
                    item
                    xs={12}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <div>
                      <PlanSwitchGroup
                        // values={['monthly', 'quarterly', 'yearly']}
                        values={['yearly', 'monthly']}
                        type={duration}
                        setType={setDuration}
                        float={'right'}
                        district={district}
                        duration={duration}
                        discountPercentForYearly={discountPercentForYearly}
                        discountPercentForQuarterly={discountPercentForQuarterly}
                        monthlyButtonDisabled={monthlyButtonDisabled}
                        product={product}
                      />
                    </div>
                  </Grid>

                  {/* <Grid container xs={12} style={{ marginBottom: '20px' }}>
                    <Grid container item xs={12}>
                      <Grid
                        item
                        xs={12}
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-end',
                          marginTop: '10px',
                          gap: '10px',
                        }}
                      >
                        <Typography
                          variant="h4"
                          style={{
                            float: 'left',
                            fontWeight: 'bold',
                            color: '#888',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-end',
                          }}
                        >
                          <span
                            style={{
                              color: discountedPrice ? '#888' : COLOR_PRIMARY_2,
                              textDecoration: discountedPrice ? 'line-through' : 'none',
                            }}
                          >
                            {exchangeRates[userLocalCurrency].symbol +
                              (duration == 'monthly'
                                ? formatCurrencyBasedOnCountryCode(
                                    parseFloat(
                                      subscriptionPriceAccordingToUserLocalCurrency[product][
                                        'monthly'
                                      ].toFixed(2)
                                    ),
                                    userLocalCurrency
                                  )
                                : formatCurrencyBasedOnCountryCode(
                                    parseFloat(
                                      (
                                        subscriptionPriceAccordingToUserLocalCurrency[product][
                                          duration
                                        ] / (duration == 'yearly' ? 12 : 3)
                                      ).toFixed(2)
                                    ),
                                    userLocalCurrency
                                  ))}
                          </span>
                          {discountedPrice && (
                            <span style={{ marginLeft: '5px', color: COLOR_PRIMARY_2 }}>
                              {exchangeRates[userLocalCurrency].symbol}
                              {duration == 'monthly'
                                ? formatCurrencyBasedOnCountryCode(
                                    discountedPrice,
                                    userLocalCurrency
                                  )
                                : formatCurrencyBasedOnCountryCode(
                                    parseFloat(
                                      (discountedPrice / (duration == 'yearly' ? 12 : 3)).toFixed(2)
                                    ),
                                    userLocalCurrency
                                  )}
                            </span>
                          )}
                          <span style={{ fontSize: '16px', marginBottom: '6px' }}>{'/month'}</span>
                        </Typography>
                      </Grid>

                      {duration == 'quarterly' && (
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            style={{
                              float: 'left',
                              fontWeight: 'bold',
                              color: '#888',
                              display: 'flex',
                              gap: 1,
                              alignItems: 'flex-end',
                            }}
                          >
                            Billed Quarterly:
                            <span
                              style={{
                                marginLeft: '2px',
                                textDecoration: discountedPrice ? 'line-through' : 'none',
                              }}
                            >
                              {exchangeRates[userLocalCurrency].symbol}
                              {formatCurrencyBasedOnCountryCode(
                                subscriptionPriceAccordingToUserLocalCurrency[product]['quarterly'],
                                userLocalCurrency
                              )}
                            </span>{' '}
                            {discountedPrice && (
                              <span style={{ marginLeft: '5px' }}>
                                {exchangeRates[userLocalCurrency].symbol}
                                {formatCurrencyBasedOnCountryCode(
                                  discountedPrice,
                                  userLocalCurrency
                                )}
                              </span>
                            )}
                            <span>{'/quarter'}</span>
                          </Typography>
                        </Grid>
                      )}

                      {duration == 'yearly' && (
                        <Grid item xs={12}>
                          <Typography
                            variant="body2"
                            style={{
                              float: 'left',
                              fontWeight: 'bold',
                              color: '#888',
                              display: 'flex',
                              gap: 1,
                              alignItems: 'flex-end',
                            }}
                          >
                            Billed Anually:
                            <span
                              style={{
                                marginLeft: '2px',
                                textDecoration: discountedPrice ? 'line-through' : 'none',
                              }}
                            >
                              {exchangeRates[userLocalCurrency].symbol}
                              {formatCurrencyBasedOnCountryCode(
                                subscriptionPriceAccordingToUserLocalCurrency[product]['yearly'],
                                userLocalCurrency
                              )}
                            </span>{' '}
                            {discountedPrice && (
                              <span style={{ marginLeft: '5px' }}>
                                {exchangeRates[userLocalCurrency].symbol}
                                {formatCurrencyBasedOnCountryCode(
                                  discountedPrice,
                                  userLocalCurrency
                                )}
                              </span>
                            )}
                            <span>{'/year'}</span>
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid> */}

                  {/* <List
                    style={{
                      padding: '10px 10px 10px 20px',
                      backgroundColor: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR + '77',
                      borderRadius: '5px',
                    }}
                  >
                    <ListItem
                      component="li"
                      style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}
                    >
                      {getProductTextCard(
                        <>
                          <span style={{ fontWeight: 'bold' }}>
                            Full access to{' '}
                            {(ProductIDOptions[product] as { markets: number }).markets > 1
                              ? 'dashboards'
                              : 'dashboard'}{' '}
                            of{' '}
                            <span style={{ color: COLOR_PRIMARY_2 }}>
                              {getMarketsText(product)}{' '}
                            </span>
                            <span style={{ color: COLOR_PRIMARY_2 }}>(Overview)</span>
                          </span>
                        </>
                      )}
                    </ListItem>
                    <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                      {getProductTextCard(
                        <>
                          <span style={{ fontWeight: 'bold' }}>
                            <span style={{ color: COLOR_PRIMARY_2 }}>Property Level</span> data and{' '}
                            <span style={{ color: COLOR_PRIMARY_2 }}>Weekly</span> data updates
                          </span>
                        </>
                      )}
                    </ListItem>
                    <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                      {getProductTextCard(
                        <>
                          <span style={{ fontWeight: 'bold' }}>
                            3 years of historical performance data of up to{' '}
                            {formatCurrency(
                              (
                                20000 * (ProductIDOptions[product] as { markets: number }).markets
                              ).toString()
                            )}{' '}
                            rentals
                          </span>
                        </>
                      )}
                    </ListItem>
                    <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                      {getProductTextCard(
                        <span style={{ fontWeight: 'bold' }}>Guest Demographics</span>
                      )}
                    </ListItem>
                    <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                      {getProductTextCard(
                        <>
                          <span style={{ color: COLOR_PRIMARY_2, fontWeight: 'bold' }}>
                            {' '}
                            Airbnb income estimator ({
                              ProductIDOptions[product].estimatorCredits
                            }{' '}
                            credits)
                          </span>
                        </>
                      )}
                    </ListItem>
                    <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                      {getProductTextCard(
                        showFullRefundPolicyStatement === false ? (
                          <>
                            <span style={{ fontWeight: 'bold' }}>
                              <span style={{ color: COLOR_PRIMARY_2 }}>
                                15-days money-back guarantee
                              </span>
                              , in the first 15 days of your first purchase, if...
                            </span>
                          </>
                        ) : (
                          <>
                            <span style={{ fontWeight: 'bold' }}>
                              <span style={{ color: COLOR_PRIMARY_2 }}>
                                15-days money-back guarantee
                              </span>
                              , in the first 15 days of your first purchase, if you don't like the
                              service or don't find it useful anymore you can request a full refund
                              we will give it to you straight away.
                              <br />
                              <br />
                              This only applies if you are first time subscriber and you have not
                              unlocked for more than 2 regions.
                              <br />
                              <br />
                              You can ask for a refund by contacting us at support@airbtics.com
                            </span>
                          </>
                        ),
                        true,
                        showFullRefundPolicyStatement,
                        setShowFullRefundPolicyStatement
                      )}
                    </ListItem>
                  </List>  */}

                  <Grid
                    item
                    xs={12}
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: matches ? '' : 'center',
                      marginTop: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        height: '1326px',
                        maxWidth: matches ? SUBSCRIPTION_MODAL_DATA_GRID_WIDTH_FOR_MOBILE : 'none',
                        width: matches ? 'none' : SUBSCRIPTION_MODAL_DATA_GRID_WIDTH,
                        backgroundColor: 'yellow',
                      }}
                    >
                      <SubscriptionModalDataGrid
                        matches={matches}
                        userLocalCurrency={userLocalCurrency}
                        user={user}
                        duration={duration}
                        subscriptionPriceAccordingToUserLocalCurrency={
                          subscriptionPriceAccordingToUserLocalCurrency
                        }
                        promoCodeDiscountsDict={promoCodeDiscountsDict}
                        product={product}
                        setProduct={setProduct}
                        promoCode={promoCode}
                        discountedPrice={discountedPrice}
                        setDiscountedPrice={setDiscountedPrice}
                        openDemoBookingForm={openDemoBookingForm}
                        paymentModalRef={paymentModalRef}
                        setFormHeadingFor={setFormHeadingFor}
                        setShowUpgradeSubscriptionModal={setShowUpgradeSubscriptionModal}
                      />
                    </Box>
                    <List style={{ maxWidth: '1230px', padding: '0px 10px' }}>
                      <ListItem
                        style={{
                          maxWidth: '1230px',
                          marginBottom: '10px',
                          paddingRight: 0,
                          paddingLeft: 0,
                        }}
                      >
                        {getProductTextCard(
                          showFullRefundPolicyStatement === false ? (
                            <>
                              <span style={{ fontWeight: 'bold' }}>
                                <span style={{ color: COLOR_PRIMARY_2 }}>
                                  15-days money-back guarantee
                                </span>
                                , in the first 15 days of your first purchase, if...
                              </span>
                            </>
                          ) : (
                            <>
                              <span style={{ fontWeight: 'bold' }}>
                                <span style={{ color: COLOR_PRIMARY_2 }}>
                                  15-days money-back guarantee
                                </span>
                                , in the first 15 days of your first purchase, if you don't like the
                                service or don't find it useful anymore you can request a full
                                refund we will give it to you straight away.
                                <br />
                                <br />
                                This only applies if you are first time subscriber and you have not
                                unlocked for more than 2 regions.
                                <br />
                                <br />
                                You can ask for a refund by contacting us at support@airbtics.com
                              </span>
                            </>
                          ),
                          true,
                          showFullRefundPolicyStatement,
                          setShowFullRefundPolicyStatement
                        )}
                      </ListItem>
                    </List>
                    <Box sx={{ width: '1230px' }}>
                      <Typography
                        variant="body1"
                        style={{
                          padding: '10px 20px',
                          backgroundColor: COLOR_ERROR_BACKGROUNG,
                          color: COLOR_TEXT_1,
                          borderRadius: '200px',
                          width: isMobile ? '300px' : '450px',
                          wordWrap: 'break-word',
                        }}
                      >
                        ⭐ Indicates exclusive features available only with Airbtics.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                {/* payment */}
                {!isSubscriptionUpgradable && (
                  <Grid
                    container
                    item
                    direction="column"
                    lg={12}
                    md={12}
                    xs={12}
                    style={{ padding: '25px' }}
                  >
                    <Grid
                      item
                      xs={12}
                      style={{
                        position: 'absolute',
                        top: '-70px',
                        right: isMobile || isMediumScreenSize ? '20px' : '12%',
                      }}
                    >
                      <img
                        src={atm}
                        style={{
                          height: isMobile ? '50px' : '100px',
                          marginTop: isMobile ? '10px' : 'none',
                        }}
                      />
                    </Grid>
                    <Grid
                      xs={12}
                      md={12}
                      lg={12}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        item
                        xs={12}
                        md={12}
                        lg={5}
                      >
                        <Typography style={{ fontWeight: 'bold', marginBottom: '20px' }}>
                          Purchase Summary
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={5}
                        style={{ marginBottom: '10px', width: '100%' }}
                      >
                        <Divider />
                      </Grid>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        item
                        xs={12}
                        md={12}
                        lg={5}
                        style={{ marginBottom: '20px' }}
                      >
                        <Typography>
                          Plan:{' '}
                          <span style={{ fontWeight: 'bold' }}>
                            {productStateVarToNameMap[product]}
                          </span>
                        </Typography>
                        <FormControl sx={{ minWidth: 120 }}>
                          <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            value={duration}
                            onChange={(event, value) => setDuration(value)}
                          >
                            <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
                            <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={12}
                        lg={5}
                        spacing={4}
                        className={classes.paymentBorder}
                      >
                        <Payment
                          countryCode={countryCode}
                          city={city}
                          district={district}
                          product={product}
                          duration={duration}
                          promoCode={
                            checkPromocodeValidity(
                              promoCode?.toUpperCase(),
                              promoCodeDiscountsDict,
                              product,
                              true
                            ) as string
                          }
                          setSubscriptionModalOpen={setSubscriptionModalOpen}
                          discountedPrice={discountedPrice}
                          countryToUnlock={countryToUnlock}
                          subscriptionPriceAccordingToUserLocalCurrency={
                            subscriptionPriceAccordingToUserLocalCurrency
                          }
                          userLocalCurrency={userLocalCurrency}
                        />
                      </Grid>
                      <Grid item xs={12} md={12} lg={5} style={{ marginTop: '20px' }}>
                        <Grid container alignItems="center">
                          <Grid item xs={isPromoCodeLoading ? 11 : 12}>
                            <TextField
                              placeholder="Promo Code (Optional)"
                              type="text"
                              className={classes.couponField}
                              inputRef={promocodeTextFieldRef}
                              onChange={handleCouponChange}
                              InputProps={
                                !isPromoCodeLoading && promoCode !== ''
                                  ? {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          {checkPromocodeValidity(
                                            promoCode?.toUpperCase(),
                                            promoCodeDiscountsDict,
                                            product
                                          ) ? (
                                            <CheckCircleIcon color="success" />
                                          ) : (
                                            <InfoTooltip
                                              title={
                                                <Card>
                                                  <CardContent
                                                    sx={{ background: COLOR_RED, color: '#fff' }}
                                                  >
                                                    <Typography variant="body1">
                                                      Promo code is invalid.
                                                    </Typography>
                                                  </CardContent>
                                                </Card>
                                              }
                                            >
                                              <ErrorIcon color="error" sx={{ cursor: 'pointer' }} />
                                            </InfoTooltip>
                                          )}
                                        </InputAdornment>
                                      ),
                                    }
                                  : {}
                              }
                            />
                          </Grid>
                          {isPromoCodeLoading && (
                            <Grid item xs={1} alignItems="center" justifyContent="center">
                              <CircularProgress size={20} />
                            </Grid>
                          )}
                        </Grid>
                        <Typography variant="caption" color="textPrimary">
                          {' '}
                          Promo discounts will be applied upon checkout and in your emailed invoice
                        </Typography>
                      </Grid>
                      <Box
                        sx={{
                          padding: '10px 20px',
                          marginTop: isMobile ? '40px' : '15px',
                          boxShadow: '0 4px 4px #ccc',
                          width: isMobile ? 'auto' : 'min(max(300px, 80%), 600px)',
                          textAlign: 'center',
                          borderRadius: '10px',
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transform: isMobile ? 'scale(0.85)' : 'scale(1)',
                          transition: 'box-shadow 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 6px 16px #888',
                          },
                        }}
                        onClick={() => {
                          postOnSlack(
                            'C05JDGPMH8T',
                            `User: ${user.email} clicked on Talk to Sales Button In the Subscription Modal.`
                          );
                          setFormHeadingFor('sales');
                          openDemoBookingForm();
                        }}
                      >
                        <div
                          style={getCTAButtonInsidePolyStyle(
                            '-50%',
                            'left',
                            '-57px',
                            -13.42,
                            '#9DD7D4'
                          )}
                        />
                        <div
                          style={getCTAButtonInsidePolyStyle(
                            isMobile ? '-160%' : '-250%',
                            'right',
                            '-55px',
                            289,
                            '#C4E7E5'
                          )}
                        />
                        <div
                          style={getCTAButtonInsidePolyStyle(
                            '-50%',
                            'right',
                            '-50px',
                            40,
                            '#9DD7D4'
                          )}
                        />
                        <Typography
                          variant="h6"
                          style={{
                            position: 'relative',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            color: COLOR_BLUE_BLACK_LIGHT,
                            zIndex: 500,
                          }}
                        >
                          Need <span style={{ color: PAYMENT_MODAL_GREEN }}>Custom Data</span> or{' '}
                          <span style={{ color: PAYMENT_MODAL_GREEN }}>API</span>? Talk to Sales
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </div>
        </Grid>
      </ClickAwayListener>
    </Backdrop>
    //   </Portal>
  );
};

export default SubscriptionModal;
